import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { IconLayoutGrid, IconPencil, IconCheck, IconGripVertical } from '@tabler/icons-react';
import { useUserPreference } from '../../../shared/useUserPreference';
import { getContrastColor } from '../../../shared/colorUtils';
import { buildCategoryDisplayCounts } from '../../../shared/categoryHierarchy';
// Reuses the .project-items-scoped category filter/edit-layout styles.
import '../../projects/components/ProjectItems.css';

// One category layout shared by every page that uses the board (project items/equipment,
// stock inventory, product catalog), so rearranging categories on one page applies to all.
const SHARED_LAYOUT_STORAGE_KEY = 'product_catalog_layout_rows';

// Colored category filter pills with an optional drag-to-rearrange row layout editor,
// shared between the warehouses items/products/stock pages.
export default function CategoryFilterBoard({ categories, selectedCategory, onCategoryChange, layoutStorageKey = SHARED_LAYOUT_STORAGE_KEY, allLabel }) {
  const { t } = useTranslation();
  const [layoutRows, setLayoutRows] = useUserPreference(layoutStorageKey, null);
  const [layoutEditing, setLayoutEditing] = useState(false);
  const safeCategories = Array.isArray(categories) ? categories : [];

  const categoryMap = useMemo(() => {
    const m = new Map();
    safeCategories.forEach(c => m.set(c.id, c));
    return m;
  }, [safeCategories]);

  // Parent categories show their own count plus every (sub)category nested under them,
  // so a parent with no directly-assigned items still reflects what's actually filterable.
  const displayCountMap = useMemo(() => buildCategoryDisplayCounts(safeCategories), [safeCategories]);

  // The layout can be shared by pages that hide some categories (e.g. empty ones in a warehouse),
  // so the saved rows (`fullRows`) keep ids that aren't shown here; edits only rearrange the
  // visible ids (`normalizedRows`) and leave the hidden ones where they were.
  const fullRows = useMemo(() => {
    if (safeCategories.length === 0) return [];
    const allIds = safeCategories.map(c => c.id);
    if (!Array.isArray(layoutRows) || layoutRows.flat().length === 0) {
      const rows = [];
      for (let i = 0; i < allIds.length; i += 3) rows.push(allIds.slice(i, i + 3));
      return rows;
    }
    const savedIdSet = new Set(layoutRows.flat());
    const missing = allIds.filter(id => !savedIdSet.has(id));
    const rows = layoutRows.map(row => [...row]);
    if (missing.length > 0) {
      if (rows.length === 0) rows.push([]);
      rows[rows.length - 1] = [...rows[rows.length - 1], ...missing];
    }
    return rows;
  }, [safeCategories, layoutRows]);

  const normalizedRows = useMemo(
    () => fullRows.map(row => row.filter(id => categoryMap.has(id))),
    [fullRows, categoryMap],
  );

  const handleDragEnd = useCallback(({ source, destination }) => {
    if (!destination) return;
    if (source.droppableId === 'rows' || destination.droppableId === 'rows') {
      if (source.index === destination.index) return;
      const rows = fullRows.map(r => [...r]);
      const [movedRow] = rows.splice(source.index, 1);
      rows.splice(destination.index, 0, movedRow);
      setLayoutRows(rows);
      return;
    }
    const srcRow = parseInt(source.droppableId.replace('row-', ''), 10);
    const dstRow = parseInt(destination.droppableId.replace('row-', ''), 10);
    if (isNaN(srcRow) || isNaN(dstRow) || !normalizedRows[srcRow] || !normalizedRows[dstRow]) return;
    if (srcRow === dstRow && source.index === destination.index) return;
    const rows = fullRows.map(r => [...r]);
    const moved = normalizedRows[srcRow][source.index];
    rows[srcRow] = rows[srcRow].filter(id => id !== moved);
    // Drag indices count visible chips only — insert before the visible chip now at that index.
    const anchor = normalizedRows[dstRow].filter(id => id !== moved)[destination.index];
    const insertAt = anchor === undefined ? rows[dstRow].length : rows[dstRow].indexOf(anchor);
    rows[dstRow].splice(insertAt, 0, moved);
    setLayoutRows(rows);
  }, [fullRows, normalizedRows, setLayoutRows]);

  const handleAddRow = useCallback(() => {
    setLayoutRows([...fullRows, []]);
  }, [fullRows, setLayoutRows]);

  const handleRemoveRow = useCallback((rowIdx) => {
    const rows = fullRows.map(r => [...r]);
    const [removed] = rows.splice(rowIdx, 1);
    if (removed.length > 0) {
      const target = Math.max(0, Math.min(rowIdx, rows.length - 1));
      if (rows.length === 0) rows.push(removed);
      else rows[target] = [...rows[target], ...removed];
    }
    setLayoutRows(rows.length > 0 ? rows : [[]]);
  }, [fullRows, setLayoutRows]);

  if (safeCategories.length === 0) return null;

  return (
    <div className="project-items">
      <div className="category-filters mb-3">
        <div className="d-flex align-items-center justify-content-between gap-1 mb-1">
          <button
            type="button"
            className={`category-filter-btn category-filter-btn--all btn btn-sm${selectedCategory === 'all' ? ' active' : ''}`}
            onClick={() => onCategoryChange('all')}
          >
            <IconLayoutGrid size={13} className="me-1 flex-shrink-0" style={{ verticalAlign: '-1px' }} />
            {allLabel || t('warehouses.items.allCategories')}
          </button>
          <button
            type="button"
            className={`btn btn-sm flex-shrink-0${layoutEditing ? ' btn-primary' : ' btn-outline-secondary'}`}
            onClick={() => setLayoutEditing(v => !v)}
          >
            {layoutEditing
              ? <><IconCheck size={13} style={{ verticalAlign: '-1px' }} className="me-1" />{t('projects.items.layoutDone')}</>
              : <><IconPencil size={13} style={{ verticalAlign: '-1px' }} className="me-1" />{t('projects.items.editLayout')}</>}
          </button>
        </div>

        {layoutEditing ? (
          <DragDropContext onDragEnd={handleDragEnd}>
            <div className="cat-edit-panel">
              <Droppable droppableId="rows" direction="vertical" type="ROW">
                {(outerProvided) => (
                  <div ref={outerProvided.innerRef} {...outerProvided.droppableProps} className="cat-edit-rows">
                    {normalizedRows.map((rowIds, rowIdx) => (
                      <Draggable key={`row-drag-${rowIdx}`} draggableId={`row-drag-${rowIdx}`} index={rowIdx}>
                        {(rowProv, rowSnap) => (
                          <div
                            ref={rowProv.innerRef}
                            {...rowProv.draggableProps}
                            className={`cat-edit-row${rowSnap.isDragging ? ' cat-edit-row--drag' : ''}`}
                          >
                            <span {...rowProv.dragHandleProps} className="cat-row-drag-handle">
                              <IconGripVertical size={14} />
                            </span>
                            <Droppable droppableId={`row-${rowIdx}`} direction="horizontal">
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.droppableProps}
                                  className={`cat-edit-droppable${snapshot.isDraggingOver ? ' cat-edit-droppable--over' : ''}`}
                                >
                                  {rowIds.map((id, i) => {
                                    const cat = categoryMap.get(id);
                                    if (!cat) return null;
                                    return (
                                      <Draggable key={id} draggableId={String(id)} index={i}>
                                        {(prov, snap) => (
                                          <div
                                            ref={prov.innerRef}
                                            {...prov.draggableProps}
                                            {...prov.dragHandleProps}
                                            className={`cat-edit-chip${snap.isDragging ? ' cat-edit-chip--drag' : ''}`}
                                          >
                                            <IconGripVertical size={12} className="cat-drag-handle" />
                                            <span className="category-filter-dot flex-shrink-0" style={{ backgroundColor: cat.color || '#6c757d' }} />
                                            <span className="cat-edit-chip-name">{cat.name}</span>
                                            <span className="category-filter-count">{displayCountMap.get(cat.id) || 0}</span>
                                          </div>
                                        )}
                                      </Draggable>
                                    );
                                  })}
                                  {provided.placeholder}
                                  {rowIds.length === 0 && !snapshot.isDraggingOver && (
                                    <span className="cat-edit-empty">{t('projects.items.dragHere')}</span>
                                  )}
                                </div>
                              )}
                            </Droppable>
                            <button type="button" className="cat-row-del-btn" onClick={() => handleRemoveRow(rowIdx)} title={t('projects.items.removeRowDef')}>×</button>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {outerProvided.placeholder}
                  </div>
                )}
              </Droppable>
              <button type="button" className="cat-add-row-btn" onClick={handleAddRow}>
                + {t('projects.items.addRowDef')}
              </button>
            </div>
          </DragDropContext>
        ) : (
          normalizedRows.map((rowIds, rowIdx) => rowIds.length === 0 ? null : (
            <div key={rowIdx} className="category-filter-row mb-1" style={{ '--cat-cols': rowIds.length || 1 }}>
              {rowIds.map(id => {
                const category = categoryMap.get(id);
                if (!category) return null;
                const isActive = selectedCategory === category.id;
                const bg = category.color || '#6c757d';
                const textColor = getContrastColor(bg);
                return (
                  <button
                    key={category.id}
                    type="button"
                    className={`category-filter-btn btn btn-sm${isActive ? ' category-filter-btn--active' : ''}`}
                    onClick={() => onCategoryChange(category.id)}
                    title={category.name}
                    style={{ '--cat-color': bg, '--cat-text': isActive ? textColor : undefined }}
                  >
                    <span className="category-filter-dot flex-shrink-0" style={{ backgroundColor: bg }} />
                    <span className="category-filter-name">{category.name}</span>
                    <span className="category-filter-count">{displayCountMap.get(category.id) || 0}</span>
                  </button>
                );
              })}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
